import { getBuildingDisplayHeight } from '@popety-io/utils'
import React, { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCustomTheme } from '../../providers/CustomThemeProvider'
import { useResolvedThemeColors } from '../../hooks/useResolvedThemeColors'
import {
  Container,
  Stack,
  Text,
  Icon,
  PageContainer,
  MainContainer,
  ContentWrapper,
  MainContent,
  MapWrapper,
  MapCloseButton,
  MobileMapToggle,
  Loading,
  Button,
  AnalysisSidebar,
  NavSectionType,
  AnalysisMapSectionDataProvider,
} from '@popety-io/ui'
import { PublicLayout } from '../../components/PublicLayout'
import { publicApiService } from '../../services/publicApiService'
import { PublicLandAnalysisContent } from './components/PublicLandAnalysisContent'
import { PublicLandAnalysisMap } from './components/PublicLandAnalysisMap'
import { PublicLandAnalysisHeader } from './components/PublicLandAnalysisHeader'
import { FullHeightContainer } from '../../components/shared'
import { PublicAnalysisProvider } from '../../providers/PublicAnalysisProvider'
import { MapSectionDataProvider, useMapSectionData } from './contexts'
import type { PublicLandAnalysis } from '@popety-io/types'

interface PublicLandAnalysisPageProps {
  isGrouping?: boolean
}

// Inner component that uses the context
const PublicLandAnalysisPageContent: React.FC<PublicLandAnalysisPageProps> = ({
  isGrouping = false,
}) => {
  const { t, i18n } = useTranslation('land-analysis')
  const { t: tCommon } = useTranslation('common')
  const { publicId: hash } = useParams<{ publicId: string }>()

  // Get map section context
  const {
    activeSection: mapActiveSection,
    sectionData,
    hoveredItemId,
    selectedItemId,
    flyToCoordinates,
    onItemClick,
    setSelectedItemId,
  } = useMapSectionData()

  // Navigation sections matching the main app exactly - with translations
  const navigationSections: NavSectionType[] = useMemo(
    () => [
      {
        title: tCommon('sidebar.sections.overview'),
        color: 'steel',
        items: [
          {
            id: 'summary',
            label: tCommon('sidebar.items.summary'),
            icon: 'bar-chart',
          },
          {
            id: 'history',
            label: tCommon('sidebar.items.history'),
            icon: 'clock',
          },
          {
            id: 'geo',
            label: tCommon('sidebar.items.geoAnalysis'),
            icon: 'map',
          },
          {
            id: 'buildings',
            label: tCommon('sidebar.items.buildingDetails'),
            icon: 'building',
          },
          {
            id: 'notes',
            label: tCommon('sidebar.items.notes'),
            icon: 'edit',
          },
          {
            id: 'files',
            label: tCommon('sidebar.items.files'),
            icon: 'file',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.development'),
        color: 'bronze',
        items: [
          {
            id: 'zoning',
            label: tCommon('sidebar.items.zoningRules'),
            icon: 'layers',
          },
          {
            id: 'restrictions',
            label: tCommon('sidebar.items.restrictions'),
            icon: 'alert-triangle',
          },
          {
            id: 'construction_permits',
            label: tCommon('sidebar.items.buildingPermits'),
            icon: 'building-permit',
          },
          {
            id: 'feasibility',
            label: tCommon('sidebar.items.feasibilityStudy'),
            icon: 'lightbulb',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.market'),
        color: 'forest',
        items: [
          {
            id: 'purchase_market',
            label: tCommon('sidebar.items.purchaseMarket'),
            icon: 'cash',
          },
          {
            id: 'rental_market',
            label: tCommon('sidebar.items.rentalMarket'),
            icon: 'home',
          },
          {
            id: 'purchase_listings',
            label: tCommon('sidebar.items.purchaseListings'),
            icon: 'listing-purchase',
          },
          {
            id: 'rental_listings',
            label: tCommon('sidebar.items.rentalListings'),
            icon: 'listing-rental',
          },
          {
            id: 'transactions',
            label: tCommon('sidebar.items.transactions'),
            icon: 'real-estate-transaction',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.environment'),
        color: 'deepBlue',
        items: [
          {
            id: 'transport',
            label: tCommon('sidebar.items.transportAccess'),
            icon: 'car',
          },
          {
            id: 'amenities',
            label: tCommon('sidebar.items.amenities'),
            icon: 'store',
          },
          {
            id: 'noise',
            label: tCommon('sidebar.items.noiseAnalysis'),
            icon: 'activity',
          },
          {
            id: 'sunlight',
            label: tCommon('sidebar.items.sunlightStudy'),
            icon: 'sun',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.socialEconomic'),
        color: 'wine',
        items: [
          {
            id: 'demographics',
            label: tCommon('sidebar.items.demographics'),
            icon: 'users',
          },
          {
            id: 'employment',
            label: tCommon('sidebar.items.employment'),
            icon: 'briefcase',
          },
          {
            id: 'taxes',
            label: tCommon('sidebar.items.taxes'),
            icon: 'receipt',
          },
          {
            id: 'electricity',
            label: tCommon('sidebar.items.electricity'),
            icon: 'zap',
          },
        ],
      },
    ],
    [tCommon]
  )

  // Theme customization
  const { updateThemeConfig } = useCustomTheme()

  // State management matching main app
  const [landData, setLandData] = useState<PublicLandAnalysis | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState('summary')
  const [isMobileMapOpen, setIsMobileMapOpen] = useState(false)
  const [mapWidth, setMapWidth] = useState(580)
  const [isResizing, setIsResizing] = useState(false)

  // Resolve theme colors from company/office data
  const resolvedThemeConfig = useResolvedThemeColors(landData)

  // Update theme when data is loaded
  useEffect(() => {
    if (landData && resolvedThemeConfig) {
      updateThemeConfig(resolvedThemeConfig)
    }
  }, [landData, resolvedThemeConfig, updateThemeConfig])

  // Memoized land ID similar to main app
  const landId = useMemo(
    () => landData?.id || landData?.land_id,
    [landData?.id, landData?.land_id]
  )

  // Filter navigation sections based on available sections
  const filteredNavigationSections = useMemo(() => {
    let sections = navigationSections.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => item.id !== 'notes' && item.id !== 'files'
      ),
    }))

    // Filter by available_sections if specified. Notes and files stay out
    // unless the share explicitly lists them.
    if (
      landData?.available_sections &&
      landData.available_sections.length > 0
    ) {
      sections = navigationSections
      sections = sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            landData.available_sections!.includes(item.id)
          ),
        }))
        .filter((section) => section.items.length > 0)
    }

    return sections
  }, [navigationSections, landData?.available_sections])

  // Open the first section that was actually shared. Summary is the default,
  // but a link that only includes notes or files would otherwise land on an
  // empty summary.
  useEffect(() => {
    const visible = filteredNavigationSections.some((section) =>
      section.items.some((item) => item.id === activeSection)
    )
    const first = filteredNavigationSections[0]?.items[0]?.id
    if (!visible && first) setActiveSection(first)
  }, [filteredNavigationSections, activeSection])

  // Fetch data on mount
  useEffect(() => {
    const fetchLandData = async () => {
      if (!hash) return

      try {
        setLoading(true)
        setError(null)
        const data = isGrouping
          ? await publicApiService.getLandGroupingAnalysis(hash)
          : await publicApiService.getLandAnalysis(hash)
        setLandData(data)

        // Set language to the share creator's preference
        const userLang = data.user?.language
        if (userLang && ['en', 'fr', 'de', 'it'].includes(userLang)) {
          i18n.changeLanguage(userLang)
        }
      } catch (err) {
        console.error('Failed to fetch land analysis:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to fetch analysis data'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchLandData()
  }, [hash, isGrouping])

  // Handle map resize (matching main app)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = window.innerWidth - e.clientX
      const constrainedWidth = Math.max(320, Math.min(800, newWidth))
      setMapWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId)
  }

  const toggleMobileMap = () => {
    setIsMobileMapOpen(!isMobileMapOpen)
  }

  if (!hash) {
    return (
      <PublicLayout>
        <Container>
          <Stack align="center" justify="center" style={{ minHeight: '60vh' }}>
            <Text variant="h3">{tCommon('validation.invalid_link')}</Text>
          </Stack>
        </Container>
      </PublicLayout>
    )
  }

  // Format address matching main app logic
  const formatAddress = () => {
    const land = landData
    if (land?.address_full) return land?.address_full
    if (land?.address_street && land?.address_postal_code) {
      return `${land?.address_street} ${land?.address_street_nb || ''}, ${land?.address_postal_code}`.trim()
    }
    return land?.municipality_name || tCommon('states.unknownLocation')
  }

  // Format area matching main app logic
  const formatArea = () => {
    const land = landData
    const area = land?.display_area ?? land?.official_area ?? land?.area
    if (!area) return tCommon('units.na')
    return `${area.toLocaleString()}m²`
  }

  return (
    <PublicLayout
      logo={landData?.office?.logo || landData?.company?.logo}
      companyName={landData?.company?.name}
      officeName={landData?.office?.name}
      creationDate={landData?.creation_date}
      title={
        landData?.user
          ? `${landData.user.first_name || landData.user.firstName || ''} ${landData.user.last_name || landData.user.lastName || ''}`
          : ''
      }
    >
      {loading && (
        <Container>
          <Stack align="center" justify="center" style={{ minHeight: '50vh' }}>
            <Loading size="large" />
            <Text variant="h4">{tCommon('loading')}</Text>
          </Stack>
        </Container>
      )}

      {error && (
        <Container>
          <Stack
            align="center"
            justify="center"
            spacing="lg"
            style={{ minHeight: '50vh' }}
          >
            <Icon name="alert-triangle" size="xl" color="error" />
            <Stack align="center" spacing="sm">
              <Text variant="h3" weight="bold">
                {t('states.errorLoading')}
              </Text>
              <Text variant="body1" color="secondary">
                {error}
              </Text>
            </Stack>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {tCommon('try_again')}
            </Button>
          </Stack>
        </Container>
      )}

      {landData && !loading && !error && (
        <PublicAnalysisProvider
          entity={landData}
          entityType="land"
          userId={landData?.hash || hash || ''}
        >
          <AnalysisMapSectionDataProvider>
            <FullHeightContainer>
              <PageContainer
                style={{ top: 0, height: '100%', position: 'relative' }}
              >
                <PublicLandAnalysisHeader
                  propertyId={landId || ''}
                  propertyTitle={`${t('header.title')} ${landData.plot_number || landData.code_number || landId || ''}`}
                  propertyAddress={formatAddress()}
                  zone={
                    landData.main_lupa_name ||
                    landData.main_lupa_type ||
                    tCommon('units.na')
                  }
                  area={formatArea()}
                  reference={
                    landData.egrid_nb ||
                    landData.plot_number ||
                    tCommon('units.na')
                  }
                  land={landData}
                />

                <MainContainer>
                  <AnalysisSidebar
                    sections={filteredNavigationSections}
                    activeSection={activeSection}
                    onSectionChange={handleSectionChange}
                  />

                  <ContentWrapper>
                    <MainContent>
                      <PublicLandAnalysisContent
                        activeSection={activeSection}
                        landData={landData}
                        hash={hash}
                        isGrouping={isGrouping}
                      />
                    </MainContent>

                    <MapWrapper $isOpen={isMobileMapOpen} $width={mapWidth}>
                      <MapCloseButton onClick={() => setIsMobileMapOpen(false)}>
                        <Icon name="x" size="medium" color="secondary" />
                      </MapCloseButton>
                      <PublicLandAnalysisMap
                        propertyId={landId || ''}
                        coordinates={
                          landData.geo_center
                            ? [landData.geo_center.lon, landData.geo_center.lat]
                            : undefined
                        }
                        onResizeStart={() => setIsResizing(true)}
                        geoJson={landData.geo_polygon}
                        buildingGeoJson={landData.building_geo_polygon}
                        buildingHeight={landData.building_height}
                        buildingDisplayHeight={getBuildingDisplayHeight(
                          landData.building_height_to_display
                        )}
                        buildingClassification={
                          landData.building_classification
                        }
                        activeSection={mapActiveSection}
                        sectionData={sectionData}
                        hoveredItemId={hoveredItemId}
                        selectedItemId={selectedItemId}
                        flyToCoordinates={flyToCoordinates}
                        onItemClick={onItemClick}
                        setSelectedItemId={setSelectedItemId}
                      />
                    </MapWrapper>
                  </ContentWrapper>
                </MainContainer>

                <MobileMapToggle onClick={toggleMobileMap}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 9m0 8V9m0 0L9 7" />
                  </svg>
                </MobileMapToggle>
              </PageContainer>
            </FullHeightContainer>
          </AnalysisMapSectionDataProvider>
        </PublicAnalysisProvider>
      )}
    </PublicLayout>
  )
}

// Wrapper component with provider
const PublicLandAnalysisPage: React.FC<PublicLandAnalysisPageProps> = ({
  isGrouping = false,
}) => {
  return (
    <MapSectionDataProvider>
      <PublicLandAnalysisPageContent isGrouping={isGrouping} />
    </MapSectionDataProvider>
  )
}

export default PublicLandAnalysisPage
